// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import { CKEditor, useCKEditorCloud } from "@ckeditor/ckeditor5-react";
import { useSearch } from "@tanstack/react-router";
import { DecoupledEditor as DecoupledEditorType } from "ckeditor5";
import { useState, useEffect, useRef, useMemo } from "react";

import { JOKE_EXTERNAL_COMMENTS, JOKE_INITIAL_DATA } from "./JokeDemoData";
import {
  PostRedlineDemoData,
  RedlineInitialData,
  REDLINE_EXTERNAL_COMMENTS,
} from "./RedlinedFinalDemoData";

// eslint-disable-next-line import/order
import { EDITABLE_DOC_LICENSE_KEY } from "../../../../config/env";
import "./EditableDocRT.css";
import { createRedline, getCKEditorJWT } from "../../../../services/projects";
import { useAppDispatch, useAppSelector } from "../../../../utils/hooks";
import {
  useEditCommentMutation,
  useNewCommentMutation,
} from "../../../../utils/queries/projects";
import { setComments } from "../../../../utils/slices/ticket";
import { useGetVisibleUsers } from "../../../../utils/tldraw/comments";

const LICENSE_KEY = EDITABLE_DOC_LICENSE_KEY;

const CLOUD_SERVICES_WEBSOCKET_URL = "wss://034va16e1ql6.cke-cs.com/ws";

export function EditableDoc({ docType }: { docType?: string }) {
  const isDemo = docType?.length > 0;
  const isJokeDemo = docType === "joke";
  const EXTERNAL_COMMENTS = isJokeDemo
    ? JOKE_EXTERNAL_COMMENTS
    : REDLINE_EXTERNAL_COMMENTS;
  const { value: ticket, comments } = useAppSelector((state) => state.ticket);
  const dispatch = useAppDispatch();
  const DOCUMENT_ID = ticket?._id || (Math.random() * 1000000).toString();
  const inlinedContent = useMemo(() => {
    if (isDemo) {
      return isJokeDemo ? JOKE_INITIAL_DATA : RedlineInitialData;
    }
    return ticket?.markup ?? "";
  }, [ticket?.markup, isDemo, isJokeDemo]);
  const editorContainerRef = useRef(null);
  const editorMenuBarRef = useRef(null);
  const editorToolbarRef = useRef(null);
  const editorRef = useRef(null);
  const editorAnnotationsRef = useRef(null);
  const editorRevisionHistoryRef = useRef(null);
  const editorRevisionHistoryEditorRef = useRef(null);
  const editorRevisionHistorySidebarRef = useRef(null);
  const [isLayoutReady, setIsLayoutReady] = useState(false);
  const [internalEditor, setInternalEditor] =
    useState<DecoupledEditorType | null>(null);
  const cloud = useCKEditorCloud({ version: "44.3.0", premium: true });
  const createCommentMutation = useNewCommentMutation();
  const editCommentMutation = useEditCommentMutation();
  const ticketUsers = useGetVisibleUsers();
  const { commentId } = useSearch({
    strict: false,
  });

  useEffect(() => {
    setIsLayoutReady(true);

    return () => setIsLayoutReady(false);
  }, []);

  const { DecoupledEditor, editorConfig } = useMemo(() => {
    if (
      cloud.status !== "success" ||
      !isLayoutReady ||
      !ticketUsers ||
      !ticket
    ) {
      return {};
    }

    const {
      DecoupledEditor,
      Plugin,
      ButtonView,
      Alignment,
      Autoformat,
      AutoImage,
      AutoLink,
      Autosave,
      BalloonToolbar,
      BlockQuote,
      BlockToolbar,
      Bold,
      Bookmark,
      CloudServices,
      Code,
      Emoji,
      Essentials,
      FindAndReplace,
      FontBackgroundColor,
      FontColor,
      FontFamily,
      FontSize,
      Heading,
      HorizontalLine,
      ImageBlock,
      ImageCaption,
      ImageEditing,
      ImageInline,
      ImageInsertViaUrl,
      ImageResize,
      ImageStyle,
      ImageTextAlternative,
      ImageToolbar,
      ImageUpload,
      ImageUtils,
      Indent,
      IndentBlock,
      Italic,
      Link,
      LinkImage,
      List,
      ListProperties,
      Mention,
      PageBreak,
      Paragraph,
      PasteFromOffice,
      RemoveFormat,
      SpecialCharacters,
      SpecialCharactersArrows,
      SpecialCharactersCurrency,
      SpecialCharactersEssentials,
      SpecialCharactersLatin,
      SpecialCharactersMathematical,
      SpecialCharactersText,
      Strikethrough,
      Subscript,
      Superscript,
      Table,
      TableCaption,
      TableCellProperties,
      TableColumnResize,
      TableProperties,
      TableToolbar,
      TextTransformation,
      TodoList,
      Underline,
      GeneralHtmlSupport,
    } = cloud.CKEditor;
    const {
      CaseChange,
      Comments,
      CommentsRepository,
      CloudServicesCommentsAdapter,
      ExportPdf,
      ExportWord,
      FormatPainter,
      ImportWord,
      MergeFields,
      MultiLevelList,
      PasteFromOfficeEnhanced,
      RealTimeCollaborativeComments,
      RealTimeCollaborativeEditing,
      RealTimeCollaborativeRevisionHistory,
      RealTimeCollaborativeTrackChanges,
      RevisionHistory,
      SlashCommand,
      Template,
      TrackChanges,
      TrackChangesData,
      TrackChangesPreview,
    } = cloud.CKEditorPremiumFeatures;

    /**
     * The `UsersIntegration` lets you manage user data and permissions.
     *
     * This is an essential feature when many users work on the same document.
     *
     * To read more about it, visit the CKEditor 5 documentation: https://ckeditor.com/docs/ckeditor5/latest/features/collaboration/users.html.
     */
    class UsersIntegration extends Plugin {
      static get requires() {
        return [
          "Users",
          "RealTimeCollaborativeComments",
          "RealTimeCollaborativeEditing",
          "RealTimeCollaborativeRevisionHistory",
          "RealTimeCollaborativeTrackChanges",
          "RevisionHistory",
        ];
      }

      static readonly pluginName = "UsersIntegration";

      init() {
        const usersPlugin = this.editor.plugins.get("Users");

        const users = [
          {
            id: "legal-ai-reviewer",
            name: "Puntt AI: Legal",
            avatar: `${window.location.origin}/LegalAvatar.png`,
          },
          {
            id: "copy-ai-reviewer",
            name: "Puntt AI: Copy",
            avatar: `${window.location.origin}/CopyAvatar.png`,
          },
          {
            id: "outside-comment",
            name: "Anonymous",
          },
        ];

        const legalAIUser = usersPlugin.getUser("legal-ai-reviewer");
        const copyAIUser = usersPlugin.getUser("copy-ai-reviewer");
        if (legalAIUser) {
          // Update user information
          legalAIUser.name = "Puntt AI: Legal";
          legalAIUser.avatar = `${window.location.origin}/LegalAvatar.png`;
          // Add other properties as needed
        }
        if (copyAIUser) {
          copyAIUser.name = "Puntt AI: Copy";
          copyAIUser.avatar = `${window.location.origin}/CopyAvatar.png`;
        }
        for (const user of users) {
          if (!usersPlugin.users.find((u) => u.id === user.id)) {
            usersPlugin.addUser(user);
          }
        }
      }
    }

    /**
     * The `CommentsIntegration` lets you synchronize comments in the document with your data source (e.g. a database).
     *
     * To read more about it, visit the CKEditor 5 documentation: https://ckeditor.com/docs/ckeditor5/latest/features/collaboration/comments/comments-integration.html.
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    class CommentsIntegration extends Plugin {
      static get requires() {
        return ["CommentsRepository", "UsersIntegration"];
      }

      static readonly pluginName = "CommentsIntegration";

      init() {
        const commentsRepositoryPlugin =
          this.editor.plugins.get("CommentsRepository");
        const channelId = this.editor.config.get("collaboration.channelId");
        if (comments.length) {
          // here i need to check if the comments are already in the commentsRepositoryPlugin
          for (const comment of comments) {
            const commentThread = commentsRepositoryPlugin.getCommentThread(
              comment.docCommentId,
            );
            if (!commentThread) {
              commentsRepositoryPlugin.addCommentThread({
                channelId: channelId,
                threadId: comment.docCommentId,
                comments: [
                  {
                    content: comment.description,
                    authorId: "outside-comment",
                  },
                ],
              });
            }
          }
        }
        commentsRepositoryPlugin.on(
          "submitCommentThread:" + channelId,
          (evt, data) => {
            if (isDemo) {
              return {
                createdAt: new Date(),
                threadId: data.threadId,
              };
            }

            const thread = commentsRepositoryPlugin.getCommentThread(
              data.threadId,
            );
            const commentContent = thread.comments._items[0]?.content || "";

            // Create payload for your database schema
            const payload = {
              description: commentContent,
              ticketId: ticket?._id,
              boardId: DOCUMENT_ID,
              boardType: "revisionBoard",
              docCommentId: thread.id,
            };

            return createCommentMutation
              .mutateAsync({
                ticketId: ticket?._id,
                payload,
              })
              .then((response) => {
                dispatch(setComments([...comments, response]));
                return {
                  createdAt: new Date(),
                  threadId: response._id,
                  commentId: response._id,
                };
              });
          },

          { priority: "low" },
        );

        commentsRepositoryPlugin.on(
          "addComment:" + channelId,
          (evt, data) => {
            const commentContent = data.content;

            if (!commentContent) return;
            if (isDemo) {
              return {
                createdAt: new Date(),
              };
            }
            const thread = commentsRepositoryPlugin.getCommentThread(
              data.threadId,
            );

            // Create payload for your database schema
            const payload = {
              description: commentContent,
              ticketId: ticket?._id,
              boardId: DOCUMENT_ID,
              boardType: "revisionBoard",
              commentId: data.threadId,
              docCommentId:
                thread.comments._items[thread.comments._items.length - 1].id,
            };

            return createCommentMutation.mutateAsync(
              {
                ticketId: ticket?._id,
                payload,
              },
              {
                onSuccess: (response) => {
                  const replaceCommentIndex = comments.findIndex(
                    (comment) => comment._id === response._id,
                  );
                  const commentsCopy = [...comments];
                  commentsCopy.splice(replaceCommentIndex, 1, data);

                  dispatch(setComments(commentsCopy));
                  return {
                    commentId: response._id,
                  };
                },
                onError: (error) => {
                  console.error("Failed to create comment:", error);
                },
              },
            );
          },
          { priority: "low" },
        );

        commentsRepositoryPlugin.on(
          "updateComment:" + channelId,
          (evt, data) => {
            if (isDemo) {
              return {
                createdAt: new Date(),
              };
            }
            const comment = comments.find(
              (c) => c.docCommentId === data.threadId,
            );
            const id =
              comment?.messages.find((m) => m.docCommentId === data.commentId)
                ?._id || comment?._id;

            editCommentMutation.mutate({
              ticketId: ticket?._id,
              messageId: id,
              payload: {
                description: data.content,
              },
            });
          },
          { priority: "low" },
        );

        commentsRepositoryPlugin.on(
          "getCommentThread:" + channelId,
          (evt, data) => {
            return {
              channelId: channelId,
              threadId: data.threadId,
              comments: [
                {
                  commentId: "comment-1",
                  authorId: "user-2",
                  content:
                    "<p>Are we sure we want to use a made-up disorder name?</p>",
                  createdAt: new Date(),
                  attributes: {},
                },
              ],
              // It defines the value on which the comment has been created initially.
              // If it is empty it will be set based on the comment marker.
              context: {
                type: "text",
                value: "Bilingual Personality Disorder",
              },
              unlinkedAt: null,
              resolvedAt: null,
              resolvedBy: null,
              attributes: {},
              isFromAdapter: true,
            };
          },
          { priority: "low" },
        );

        commentsRepositoryPlugin.on(
          "resolveCommentThread:" + channelId,
          (evt, data) => {
            const usersPlugin = this.editor.plugins.get("Users");
            const user = usersPlugin.me;
            const comment = comments.find(
              (c) => c.docCommentId === data.threadId,
            );
            editCommentMutation.mutate({
              ticketId: ticket?._id,
              messageId: comment?._id,
              payload: {
                disposition: "resolved",
                description: comment?.description,
              },
            });
            return {
              resolvedAt: new Date(), // Should be set on the server side.
              resolvedBy: user?.id, // Should be set on the server side.
            };
          },
          { priority: "low" },
        );
      }
    }

    /**
     * The `TrackChangesIntegration` lets you synchronize suggestions added to the document with your data source (e.g. a database).
     *
     * To read more about it, visit the CKEditor 5 documentation: https://ckeditor.com/docs/ckeditor5/latest/features/collaboration/track-changes/track-changes-integration.html.
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    class TrackChangesIntegration extends Plugin {
      static get requires() {
        return [
          "TrackChanges",
          "TrackChangesData",
          "TrackChangesPreview",
          "Users",
          "RealTimeCollaborativeComments",
          "RealTimeCollaborativeEditing",
          "RealTimeCollaborativeRevisionHistory",
          "RealTimeCollaborativeTrackChanges",
          "RevisionHistory",
        ];
      }

      static readonly pluginName = "TrackChangesIntegration";

      init() {
        const trackChangesPlugin = this.editor.plugins.get("TrackChanges");
        const channelId = this.editor.config.get("collaboration.channelId");
        if (ticket?.redlines?.length) {
          for (const redline of ticket.redlines) {
            trackChangesPlugin.addSuggestion({
              id: redline._id,
              type: redline.type,
              authorId: "outside-comment",
              createdAt: new Date(),
            });
          }
        }
        // Set up event listeners with proper channelId namespacing
        this._setupEventListeners(trackChangesPlugin, channelId);
      }

      _setupEventListeners(trackChangesPlugin) {
        // Create a debounce function to prevent multiple calls for the same suggestion
        const processedSuggestions = new Set();
        const debouncedCreateRedline = (suggestionId, suggestion) => {
          // If we've already processed this suggestion in this session, skip it
          if (processedSuggestions.has(suggestionId)) return;

          // Mark this suggestion as processed
          processedSuggestions.add(suggestionId);

          createRedline({
            ticketId: ticket?._id,
            // the payload needs to match the schema:
            payload: {
              type: suggestion.type,
              data: { text: suggestion.text },
              attributes: {},
              boardId: DOCUMENT_ID,
              commentThreadId: suggestion.commentThread?.id,
              _id: suggestion.id,
            },
          });
        };

        // Monitor suggestion markers
        this.editor.model.on("applyOperation", (evt, args) => {
          if (
            args[0].type === "marker" &&
            args[0].name.startsWith("suggestion:")
          ) {
            // Use setTimeout as a simple debounce mechanism
            setTimeout(() => {
              const suggestions = trackChangesPlugin.getSuggestions();
              // Find the suggestion whose id is included in the args[0].name
              const suggestion = suggestions.find((suggestion) =>
                args[0].name.includes(suggestion.id),
              );

              if (
                suggestion &&
                !ticket?.redlines?.find(
                  (redline) => redline._id === suggestion.id,
                )
              ) {
                debouncedCreateRedline(suggestion.id, suggestion);
              }
            }, 3000);
          }
        });
      }
    }

    /**
     * The `AnnotationsSidebarToggler` plugin adds an icon to the right side of the editor.
     *
     * It allows to toggle the right annotations bar visibility.
     */
    class AnnotationsSidebarToggler extends Plugin {
      static get requires() {
        return ["AnnotationsUIs"];
      }

      static readonly pluginName = "AnnotationsSidebarToggler";

      init() {
        this.toggleButton = new ButtonView(this.editor.locale);

        const NON_COLLAPSE_ANNOTATION_ICON =
          '<svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" transform="matrix(-1,0,0,1,0,0)"><path d="M11.463 5.187a.888.888 0 1 1 1.254 1.255L9.16 10l3.557 3.557a.888.888 0 1 1-1.254 1.255L7.26 10.61a.888.888 0 0 1 .16-1.382l4.043-4.042z"></path></svg>';
        const COLLAPSE_ANNOTATION_ICON =
          '<svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" transform="matrix(1,0,0,1,0,0)"><path d="M11.463 5.187a.888.888 0 1 1 1.254 1.255L9.16 10l3.557 3.557a.888.888 0 1 1-1.254 1.255L7.26 10.61a.888.888 0 0 1 .16-1.382l4.043-4.042z"/></svg>';

        const annotationsUIsPlugin = this.editor.plugins.get("AnnotationsUIs");
        const annotationsContainer =
          this.editor.config.get("sidebar.container");
        const sidebarContainer = annotationsContainer.parentElement;

        this.toggleButton.set({
          label: "Toggle annotations sidebar",
          tooltip: "Hide annotations sidebar",
          tooltipPosition: "se",
          icon: COLLAPSE_ANNOTATION_ICON,
        });

        this.toggleButton.on("execute", () => {
          // Toggle a CSS class on the annotations sidebar container to manage the visibility of the sidebar.
          annotationsContainer.classList.toggle("ck-hidden");

          // Change the look of the button to reflect the state of the annotations container.
          if (annotationsContainer.classList.contains("ck-hidden")) {
            this.toggleButton.icon = NON_COLLAPSE_ANNOTATION_ICON;
            this.toggleButton.tooltip = "Show annotations sidebar";
            annotationsUIsPlugin.switchTo("inline");
          } else {
            this.toggleButton.icon = COLLAPSE_ANNOTATION_ICON;
            this.toggleButton.tooltip = "Hide annotations sidebar";
            annotationsUIsPlugin.switchTo("wideSidebar");
          }

          // Keep the focus in the editor whenever the button is clicked.
          this.editor.editing.view.focus();
        });

        this.toggleButton.render();

        sidebarContainer.insertBefore(
          this.toggleButton.element,
          annotationsContainer,
        );
      }

      destroy() {
        this.toggleButton.element.remove();

        return super.destroy();
      }
    }

    class RevisionHistoryAutosaveIntegration extends Plugin {
      static get requires() {
        return ["RevisionTracker", "RevisionHistory"];
      }

      init() {
        this._saveAfter = 10;
        this._autosaveCount = 1; // Current autosave counter.
        this._lastCreatedAt = null; // Revision `createdAt` value, when the revision was last autosaved.
      }

      async autosave() {
        const revisionTracker = this.editor.plugins.get("RevisionTracker");
        const currentRevision = revisionTracker.currentRevision;

        if (currentRevision.createdAt > this._lastCreatedAt) {
          // Revision was saved or updated in the meantime by a different source (not autosave).
          // Reset the counter.
          this._autosaveCount = 1;
        }

        if (this._autosaveCount === this._saveAfter) {
          // We reached the count. Save all changes as a new revision. Reset the counter.
          await revisionTracker.saveRevision({
            name: new Date().toISOString(),
          });

          this._autosaveCount = 1;
          this._lastCreatedAt = currentRevision.createdAt;
        } else {
          await revisionTracker.update();

          if (currentRevision.createdAt > this._lastCreatedAt) {
            this._autosaveCount++;
            this._lastCreatedAt = currentRevision.createdAt;
          }
        }

        return true;
      }
    }

    return {
      DecoupledEditor,
      editorConfig: {
        toolbar: {
          items: [
            "previousPage",
            "nextPage",
            "|",
            "revisionHistory",
            "trackChanges",
            "comment",
            "|",
            "insertMergeField",
            "previewMergeFields",
            "|",
            "formatPainter",
            "|",
            "heading",
            "|",
            "fontSize",
            "fontFamily",
            "fontColor",
            "fontBackgroundColor",
            "|",
            "bold",
            "italic",
            "underline",
            "|",
            "link",
            "insertTable",
            "blockQuote",
            "|",
            "alignment",
            "|",
            "bulletedList",
            "numberedList",
            "multiLevelList",
            "todoList",
            "outdent",
            "indent",
          ],
          shouldNotGroupWhenFull: false,
        },
        plugins: [
          Alignment,
          Autoformat,
          AutoImage,
          AutoLink,
          Autosave,
          BalloonToolbar,
          BlockQuote,
          BlockToolbar,
          Bold,
          Bookmark,
          CaseChange,
          CloudServices,
          CloudServicesCommentsAdapter,
          Code,
          Comments,
          CommentsRepository,
          Emoji,
          Essentials,
          ExportPdf,
          ExportWord,
          FindAndReplace,
          FontBackgroundColor,
          FontColor,
          FontFamily,
          FontSize,
          FormatPainter,
          GeneralHtmlSupport,
          Heading,
          HorizontalLine,
          ImageBlock,
          ImageCaption,
          ImageEditing,
          ImageInline,
          ImageInsertViaUrl,
          ImageResize,
          ImageStyle,
          ImageTextAlternative,
          ImageToolbar,
          ImageUpload,
          ImageUtils,
          ImportWord,
          Indent,
          IndentBlock,
          Italic,
          Link,
          LinkImage,
          List,
          ListProperties,
          Mention,
          MergeFields,
          MultiLevelList,
          PageBreak,
          Paragraph,
          PasteFromOffice,
          PasteFromOfficeEnhanced,

          RealTimeCollaborativeComments,
          RealTimeCollaborativeEditing,
          RealTimeCollaborativeRevisionHistory,
          RealTimeCollaborativeTrackChanges,
          RemoveFormat,
          RevisionHistory,
          SlashCommand,
          SpecialCharacters,
          SpecialCharactersArrows,
          SpecialCharactersCurrency,
          SpecialCharactersEssentials,
          SpecialCharactersLatin,
          SpecialCharactersMathematical,
          SpecialCharactersText,
          Strikethrough,
          Subscript,
          Superscript,
          Table,
          TableCaption,
          TableCellProperties,
          TableColumnResize,
          TableProperties,
          TableToolbar,
          Template,
          TextTransformation,
          TodoList,
          TrackChanges,
          TrackChangesData,
          TrackChangesPreview,
          Underline,
        ],
        extraPlugins: [
          UsersIntegration,
          AnnotationsSidebarToggler,
          CommentsIntegration,
          TrackChangesIntegration,
          RevisionHistoryAutosaveIntegration,
        ],
        htmlSupport: {
          allow: [
            {
              name: /.*/,
              attributes: true,
              classes: true,
              styles: true,
            },
          ],
        },
        balloonToolbar: [
          "comment",
          "|",
          "bold",
          "italic",
          "|",
          "link",
          "|",
          "bulletedList",
          "numberedList",
        ],
        blockToolbar: [
          "comment",
          "|",
          "fontSize",
          "fontColor",
          "fontBackgroundColor",
          "|",
          "bold",
          "italic",
          "|",
          "link",
          "insertTable",
          "|",
          "bulletedList",
          "numberedList",
          "outdent",
          "indent",
        ],
        cloudServices: {
          tokenUrl: () => getCKEditorJWT(),
          webSocketUrl: CLOUD_SERVICES_WEBSOCKET_URL,
        },
        collaboration: {
          channelId: DOCUMENT_ID,
        },
        autosave: {
          save: async (editor) => {
            return editor.plugins
              .get(RevisionHistoryAutosaveIntegration)
              .autosave();
          },
        },
        comments: {
          editorConfig: {
            extraPlugins: [Autoformat, Bold, Italic, List, Mention],
            mention: {
              feeds: [
                {
                  marker: "@",
                  feed:
                    ticketUsers?.map((user) => ({
                      id: `@${user.email}`,
                      fullName: user.name,
                      text: user.name,
                    })) || [],
                },
              ],
            },
          },
        },
        exportPdf: {
          stylesheets: [
            /* This path should point to the content stylesheets on your assets server. */
            /* See: https://ckeditor.com/docs/ckeditor5/latest/features/converters/export-pdf.html */
            "./export-style.css",
            /* Export PDF needs access to stylesheets that style the content. */
            "https://cdn.ckeditor.com/ckeditor5/44.3.0/ckeditor5.css",
            "https://cdn.ckeditor.com/ckeditor5-premium-features/44.3.0/ckeditor5-premium-features.css",
          ],
          fileName: "export-pdf-demo.pdf",
          converterOptions: {
            format: "A4",
            margin_top: "20mm",
            margin_bottom: "20mm",
            margin_right: "12mm",
            margin_left: "12mm",
            page_orientation: "portrait",
          },
        },
        exportWord: {
          stylesheets: [
            /* This path should point to the content stylesheets on your assets server. */
            /* See: https://ckeditor.com/docs/ckeditor5/latest/features/converters/export-word.html */
            "./export-style.css",
            /* Export Word needs access to stylesheets that style the content. */
            "https://cdn.ckeditor.com/ckeditor5/44.3.0/ckeditor5.css",
            "https://cdn.ckeditor.com/ckeditor5-premium-features/44.3.0/ckeditor5-premium-features.css",
          ],
          fileName: "export-word-demo.docx",
          converterOptions: {
            document: {
              orientation: "portrait",
              size: "A4",
              margins: {
                top: "20mm",
                bottom: "20mm",
                right: "12mm",
                left: "12mm",
              },
            },
          },
        },
        fontFamily: {
          supportAllValues: true,
        },
        fontSize: {
          options: [10, 12, 14, "default", 18, 20, 22],
          supportAllValues: true,
        },
        heading: {
          options: [
            {
              model: "paragraph",
              title: "Paragraph",
              class: "ck-heading_paragraph",
            },
            {
              model: "heading1",
              view: "h1",
              title: "Heading 1",
              class: "ck-heading_heading1",
            },
            {
              model: "heading2",
              view: "h2",
              title: "Heading 2",
              class: "ck-heading_heading2",
            },
            {
              model: "heading3",
              view: "h3",
              title: "Heading 3",
              class: "ck-heading_heading3",
            },
            {
              model: "heading4",
              view: "h4",
              title: "Heading 4",
              class: "ck-heading_heading4",
            },
            {
              model: "heading5",
              view: "h5",
              title: "Heading 5",
              class: "ck-heading_heading5",
            },
            {
              model: "heading6",
              view: "h6",
              title: "Heading 6",
              class: "ck-heading_heading6",
            },
          ],
        },
        image: {
          toolbar: [
            "toggleImageCaption",
            "imageTextAlternative",
            "|",
            "imageStyle:inline",
            "imageStyle:wrapText",
            "imageStyle:breakText",
            "|",
            "resizeImage",
          ],
        },
        initialData: inlinedContent,
        licenseKey: LICENSE_KEY,
        link: {
          addTargetToExternalLinks: true,
          defaultProtocol: "https://",
          decorators: {
            toggleDownloadable: {
              mode: "manual",
              label: "Downloadable",
              attributes: {
                download: "file",
              },
            },
          },
        },
        list: {
          properties: {
            styles: true,
            startIndex: true,
            reversed: true,
          },
        },
        mention: {
          feeds: [
            {
              marker: "@",
              feed: [],
            },
          ],
        },
        menuBar: {
          isVisible: true,
        },
        mergeFields: {
          /* Read more: https://ckeditor.com/docs/ckeditor5/latest/features/merge-fields.html#configuration */
        },
        pagination: {
          pageWidth: "21cm",
          pageHeight: "29.7cm",
          pageMargins: {
            top: "20mm",
            bottom: "20mm",
            right: "12mm",
            left: "12mm",
          },
        },
        placeholder: "Type or paste your content here!",

        revisionHistory: {
          editorContainer: editorContainerRef.current,
          viewerContainer: editorRevisionHistoryRef.current,
          viewerEditorElement: editorRevisionHistoryEditorRef.current,
          viewerSidebarContainer: editorRevisionHistorySidebarRef.current,
          resumeUnsavedRevision: true,
        },
        sidebar: {
          container: editorAnnotationsRef.current,
        },
        table: {
          contentToolbar: [
            "tableColumn",
            "tableRow",
            "mergeTableCells",
            "tableProperties",
            "tableCellProperties",
          ],
        },
        template: {
          definitions: [
            {
              title: "Introduction",
              description: "Simple introduction to an article",
              icon: '<svg width="45" height="45" viewBox="0 0 45 45" fill="none" xmlns="http://www.w3.org/2000/svg">\n    <g id="icons/article-image-right">\n        <rect id="icon-bg" width="45" height="45" rx="2" fill="#A5E7EB"/>\n        <g id="page" filter="url(#filter0_d_1_507)">\n            <path d="M9 41H36V12L28 5H9V41Z" fill="white"/>\n            <path d="M35.25 12.3403V40.25H9.75V5.75H27.7182L35.25 12.3403Z" stroke="#333333" stroke-width="1.5"/>\n        </g>\n        <g id="image">\n            <path id="Rectangle 22" d="M21.5 23C21.5 22.1716 22.1716 21.5 23 21.5H31C31.8284 21.5 32.5 22.1716 32.5 23V29C32.5 29.8284 31.8284 30.5 31 30.5H23C22.1716 30.5 21.5 29.8284 21.5 29V23Z" fill="#B6E3FC" stroke="#333333"/>\n            <path id="Vector 1" d="M24.1184 27.8255C23.9404 27.7499 23.7347 27.7838 23.5904 27.9125L21.6673 29.6268C21.5124 29.7648 21.4589 29.9842 21.5328 30.178C21.6066 30.3719 21.7925 30.5 22 30.5H32C32.2761 30.5 32.5 30.2761 32.5 30V27.7143C32.5 27.5717 32.4391 27.4359 32.3327 27.3411L30.4096 25.6268C30.2125 25.451 29.9127 25.4589 29.7251 25.6448L26.5019 28.8372L24.1184 27.8255Z" fill="#44D500" stroke="#333333" stroke-linejoin="round"/>\n            <circle id="Ellipse 1" cx="26" cy="25" r="1.5" fill="#FFD12D" stroke="#333333"/>\n        </g>\n        <rect id="Rectangle 23" x="13" y="13" width="12" height="2" rx="1" fill="#B4B4B4"/>\n        <rect id="Rectangle 24" x="13" y="17" width="19" height="2" rx="1" fill="#B4B4B4"/>\n        <rect id="Rectangle 25" x="13" y="21" width="6" height="2" rx="1" fill="#B4B4B4"/>\n        <rect id="Rectangle 26" x="13" y="25" width="6" height="2" rx="1" fill="#B4B4B4"/>\n        <rect id="Rectangle 27" x="13" y="29" width="6" height="2" rx="1" fill="#B4B4B4"/>\n        <rect id="Rectangle 28" x="13" y="33" width="16" height="2" rx="1" fill="#B4B4B4"/>\n    </g>\n    <defs>\n        <filter id="filter0_d_1_507" x="9" y="5" width="28" height="37" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">\n            <feFlood flood-opacity="0" result="BackgroundImageFix"/>\n            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>\n            <feOffset dx="1" dy="1"/>\n            <feComposite in2="hardAlpha" operator="out"/>\n            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.29 0"/>\n            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_1_507"/>\n            <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_1_507" result="shape"/>\n        </filter>\n    </defs>\n</svg>\n',
              data: "<h2>Introduction</h2><p>In today's fast-paced world, keeping up with the latest trends and insights is essential for both personal growth and professional development. This article aims to shed light on a topic that resonates with many, providing valuable information and actionable advice. Whether you're seeking to enhance your knowledge, improve your skills, or simply stay informed, our comprehensive analysis offers a deep dive into the subject matter, designed to empower and inspire our readers.</p>",
            },
          ],
        },
      },
    };
  }, [cloud, isLayoutReady, inlinedContent]);

  useEffect(() => {
    if (!internalEditor || cloud.status != "success") return;

    const editor = internalEditor;
    const commentsRepo = editor.plugins.get("CommentsRepository");

    if (commentId) {
      commentsRepo.setActiveCommentThread(
        comments.find((c) => c._id == commentId)?.docCommentId,
      );
    }
    if (!isDemo) return;

    function findParagraphAndPositions(
      editor: any,
      targetParagraphNumber: number,
      startOffset: number,
      endOffset?: number,
    ) {
      const root = editor.model.document.getRoot();

      // Make sure we have content to work with
      if (!root || root.childCount === 0) {
        console.warn("No content found in editor");
        return null;
      }

      // Find the target paragraph
      let targetParagraph = null;
      let paragraphCount = 0;

      // Iterate through root children to find the target paragraph
      for (const child of root.getChildren()) {
        if (child.name === "paragraph") {
          paragraphCount++;
          if (paragraphCount === targetParagraphNumber) {
            targetParagraph = child;
            break;
          }
        }
      }

      if (!targetParagraph) {
        console.warn(
          `Paragraph ${targetParagraphNumber} not found in the document`,
        );
        return null;
      }

      // Get the text length of the paragraph to ensure we don't exceed it
      const paragraphText = Array.from(targetParagraph.getChildren())
        .filter((item) => item.is("$text"))
        .reduce((text, item) => text + item.data, "");

      // Determine safe offsets
      const safeStartOffset = Math.min(startOffset, paragraphText.length);
      const safeEndOffset = endOffset
        ? Math.min(endOffset, paragraphText.length)
        : undefined;

      // Create positions
      const startPosition = editor.model.createPositionAt(
        targetParagraph,
        safeStartOffset,
      );
      const endPosition = safeEndOffset
        ? editor.model.createPositionAt(targetParagraph, safeEndOffset)
        : undefined;

      return { targetParagraph, startPosition, endPosition };
    }

    EXTERNAL_COMMENTS.forEach(({ id, text, paragraph, start, end }) => {
      const { startPosition, endPosition } = findParagraphAndPositions(
        editor,
        paragraph,
        start,
        end,
      );

      if (startPosition && endPosition) {
        editor.model.change((writer) => {
          const range = writer.createRange(startPosition, endPosition);
          const channelId = editor.config.get("collaboration.channelId");

          commentsRepo.addCommentThread({
            channelId,
            threadId: id,
            comments: [
              {
                commentId: `comment-${id}`,
                authorId: !isJokeDemo
                  ? "legal-ai-reviewer"
                  : "copy-ai-reviewer",
                content: `${text}`,
                createdAt: new Date(),
              },
            ],
          });

          writer.addMarker(`comment:${id}`, {
            range,
            usingOperation: true,
            affectsData: true,
          });
        });
      }
    });
    if (!isJokeDemo) {
      const trackChanges = editor.plugins.get("TrackChanges");

      trackChanges.addSuggestion({
        id: `suggestion-1`,
        type: "insertion",
        authorId: "legal-ai-reviewer",
        createdAt: new Date(),
        data: null,
        attributes: {},
      });
      trackChanges.addSuggestion({
        id: `suggestion-2`,
        type: "deletion",
        authorId: "legal-ai-reviewer",
        createdAt: new Date(),
        data: null,
        attributes: {},
      });
      trackChanges.addSuggestion({
        id: `suggestion-3`,
        type: "insertion",
        authorId: "legal-ai-reviewer",
        createdAt: new Date(),
        data: null,
        attributes: {},
      });
      trackChanges.addSuggestion({
        id: `suggestion-4`,
        type: "deletion",
        authorId: "legal-ai-reviewer",
        createdAt: new Date(),
        data: null,
        attributes: {},
      });

      editor.data.set(PostRedlineDemoData, {
        suppressErrorInCollaboration: true,
      });
    }
  }, [isLayoutReady, internalEditor, isDemo, commentId]);

  return (
    <div className="main-container">
      <div
        className="editor-container editor-container_document-editor editor-container_include-annotations editor-container_include-pagination"
        ref={editorContainerRef}
      >
        <div
          className="editor-container__menu-bar"
          ref={editorMenuBarRef}
        ></div>
        <div className="editor-container__toolbar" ref={editorToolbarRef}></div>
        <div className="editor-container__editor-wrapper">
          <div className="editor-container__editor">
            <div ref={editorRef}>
              {DecoupledEditor && editorConfig && (
                <CKEditor
                  onReady={(editor) => {
                    if (editorToolbarRef.current) {
                      editorToolbarRef.current.appendChild(
                        editor.ui.view.toolbar.element,
                      );
                    }
                    if (editorMenuBarRef.current) {
                      editorMenuBarRef.current.appendChild(
                        editor.ui.view.menuBarView.element,
                      );
                    }
                    setInternalEditor(editor);
                  }}
                  onAfterDestroy={() => {
                    if (editorToolbarRef.current) {
                      Array.from(editorToolbarRef.current.children).forEach(
                        (child) => child.remove(),
                      );
                    }
                    if (editorMenuBarRef.current) {
                      Array.from(editorMenuBarRef.current.children).forEach(
                        (child) => child.remove(),
                      );
                    }
                  }}
                  editor={DecoupledEditor}
                  config={editorConfig}
                />
              )}
            </div>
          </div>
          <div className="editor-container__sidebar">
            <div ref={editorAnnotationsRef}></div>
          </div>
        </div>
      </div>
      <div className="revision-history" ref={editorRevisionHistoryRef}>
        <div className="revision-history__wrapper">
          <div
            className="revision-history__editor"
            ref={editorRevisionHistoryEditorRef}
          ></div>
          <div
            className="revision-history__sidebar"
            ref={editorRevisionHistorySidebarRef}
          ></div>
        </div>
      </div>
    </div>
  );
}
